<template>
    <div>
        <rkm-table
            :fields="fields.datatables.servicos"
            :items="data.form.servicosEnfermagem.concat(data.model.servicosEnfermagem)"
            hide-filters
            hide-pagination
            tag="div"
        >
            <template v-slot:cell(info)="row">
                <div v-if="Number.isInteger(row.item.id)">
                    <rkm-icon
                        :id="`pronto-atendimento-servico-enfermagem-info-${row.item.id}`"
                        class="mr-2"
                        icon="info-circle"
                    />

                    <rkm-popover
                        :target="`pronto-atendimento-servico-enfermagem-info-${row.item.id}`"
                        triggers="hover"
                    >
                        <div>{{ row.item.centroCusto.setor.nome }}</div>
                        <div><rkm-icon icon="user" /> {{ row.item.lotacao.profissional.nome }} - {{ row.item.lotacao.ocupacao.nome }}</div>
                        <div>Class. risco: {{ row.item.classificacaoRisco.nome }}</div>
                        <div v-if="row.item.horarioRealizacao && row.item.profissional">Realizado por: {{ row.item.profissional.nome }}</div>
                    </rkm-popover>

                    {{ row.item.dataHora }}
                </div>
            </template>

            <template v-slot:cell(codigo)="row">
                <div v-if="row.item.procedimento.codigo">
                    {{ row.item.procedimento.codigo | formatProcedimento }}
                </div>
            </template>

            <template v-slot:cell(status)="row">
                <rkm-form-radio-group
                    buttons
                    v-model="row.item.status.id"
                    :validations="validations.status"
                    name="Status"
                >
                    <rkm-form-radio
                        color="success"
                        :outline="row.item.disabled ? !(row.item.status.id === enums.status.REALIZADO) : true"
                        :value="enums.status.REALIZADO"
                        :disabled="row.item.disabled"
                        v-rkm-tooltip.hover
                        title="Realizado"
                    >
                        <rkm-icon icon="check" />
                    </rkm-form-radio>

                    <rkm-form-radio
                        color="primary"
                        :outline="row.item.disabled ? !(row.item.status.id === enums.status.SOLICITADO) : true"
                        @click="clear(row.item)"
                        :value="enums.status.SOLICITADO"
                        :disabled="row.item.disabled"
                        v-rkm-tooltip.hover
                        title="Solicitado"
                    >
                        <rkm-icon icon="hourglass" />
                    </rkm-form-radio>

                    <rkm-form-radio
                        color="danger"
                        :outline="row.item.disabled ? !(row.item.status.id === enums.status.NAO_REALIZADO) : true"
                        @click="clear(row.item)"
                        :value="enums.status.NAO_REALIZADO"
                        :disabled="row.item.disabled"
                        v-rkm-tooltip.hover
                        title="Não realizado"
                    >
                        <rkm-icon icon="times" />
                    </rkm-form-radio>
                </rkm-form-radio-group>
            </template>

            <template v-slot:cell(horario)="row">
                <rkm-form-time-picker
                    name="Horário"
                    class="m-0"
                    v-model="row.item.horarioRealizacao"
                    @blur="validateTime(row.item)"
                    @hidden="validateTime(row.item)"
                    :disabled="row.item.disabled || row.item.status.id !== enums.status.REALIZADO"
                    :validations="{ required: row.item.status.id === enums.status.REALIZADO }"
                />
            </template>

            <template v-slot:cell(cid)="row">
                <rkm-form-input
                    :value="row.item.cid ? formatCid(row.item.cid.codigo) : ''"
                    :validations="{ required: row.item.status.id === enums.status.REALIZADO && !!row.item.procedimento.hasCidPrincipal }"
                    class="m-0"
                    name="CID"
                    readonly
                    :disabled="row.item.disabled"
                    no-validation-message
                >
                    <rkm-input-group-append slot="append">
                        <rkm-button
                            :disabled="row.item.disabled || row.item.status.id !== enums.status.REALIZADO"
                            color="primary"
                            title="Buscar CID"
                            @click.stop="$refs[`ModalBuscaCid-${row.item.id}`].open(getCidParams(row.item))"
                        >
                            <rkm-icon icon="search" />
                        </rkm-button>
                    </rkm-input-group-append>

                    <rkm-input-group-append slot="append">
                        <rkm-button
                            :disabled="row.item.disabled || row.item.status.id !== enums.status.REALIZADO"
                            color="outline-danger"
                            title="Limpar CID"
                            @click.stop="row.item.cid = null"
                        >
                            <rkm-icon icon="times" />
                        </rkm-button>
                    </rkm-input-group-append>
                </rkm-form-input>

                <ModalBuscaCid
                    :ref="`ModalBuscaCid-${row.item.id}`"
                    @select="row.item.cid = $event"
                />
            </template>

            <template v-slot:cell(actions)="row">
                <rkm-button
                    :disabled="Number.isInteger(row.item.id)"
                    @click="$refs.Modal.open(row.item)"
                    color="primary"
                    title="Editar"
                >
                    <rkm-icon icon="pen" />
                </rkm-button>

                <rkm-button
                    :disabled="Number.isInteger(row.item.id)"
                    @click="handleDestroy(row.item)"
                    title="Excluir"
                    color="danger"
                >
                    <rkm-icon icon="trash" />
                </rkm-button>
            </template>
        </rkm-table>

        <Modal
            :data="data"
            ref="Modal"
        />
    </div>
</template>

<script>
// Components.
import Modal from './Modal'
import ModalBuscaCid from '@/components/sigtap/cid/busca/Modal'

// Enums
import status from '@/enums/pronto-atendimento/statusExame'

// Filters.
import formatsFilters from '@/filters/formats'

// Mixins.
import { repository, formats } from '@/mixins'

import moment from 'moment'

import { mapActions } from 'vuex'

export default {
    components: { Modal, ModalBuscaCid },
    computed: {
        validations() {

            return {
                status: { required: true }
            }
        },
        fields() {

            return {
                datatables: {
                    servicos: [
                        { key: 'info', label: 'Data / Hora', class: 'col-min' },
                        { key: 'codigo', label: 'Código', class: 'col-min' },
                        { key: 'procedimento.nome', label: 'Nome' },
                        { key: 'status', label: 'Status', class: 'col-min text-center' },
                        { key: 'horario', label: 'Realizado às', class: 'text-center' },
                        { key: 'cid', label: 'CID', class: 'text-center' },
                        { key: 'actions', label: 'Ações', class: 'col-min text-center' },
                    ]
                }
            }
        },
    },
    created() {

        this.repositories = {
            dataHora: this.getConectaSusRepository('dataHora')
        }
    },
    data: () => ({
        enums: {
            status
        },
    }),
    filters: {
        ...formatsFilters,
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        getCidParams(item) {

            if (item.procedimento.hasCidPrincipal) {

                return {
                    principal: true,
                    procedimentoId: item.procedimento.id
                }
            }

            return {}
        },
        clear(item) {

            if (!item.disabled) {

                item.cid = null
                item.horarioRealizacao = null
            }
        },
        validateTime(servico) {

            this.$nextTick().then(() => {

                if (servico.horarioRealizacao) {

                    const horarioRealizacao = moment(servico.horarioRealizacao, 'HH:mm')

                    this.setLoading()

                    this.repositories.dataHora.get({ formato: 'd/m/Y H:i' })
                        .then(({ data }) => {

                            const dataHoraAtual = moment(data, 'DD/MM/YYYY HH:mm')
                            const dataHoraRealizacao = dataHoraAtual.clone()

                            dataHoraRealizacao.hour(horarioRealizacao.hour())
                            dataHoraRealizacao.minute(horarioRealizacao.minute())

                            if (dataHoraRealizacao > dataHoraAtual) {

                                this.showValidationNotification(
                                    'A hora inserida no serviço não pode ser maior que a hora atual.'
                                )

                                servico.horarioRealizacao = null
                            }
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                }
            })
        },
        handleDestroy(item) {
            const destroy = () => new Promise(resolve => {
                const index = this.data.model.servicosEnfermagem.findIndex(procedimento => procedimento.id === item.id)
                resolve(this.data.model.servicosEnfermagem.splice(index, 1))
            })

            this.showDeleteConfirmation('Tem certeza que deseja excluir?', destroy)
        },
    },
    mixins: [repository, formats],
    props: {
        data: { type: Object, required: true },
    },
}
</script>